import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Accordion, Table, Form, Alert, Spinner, Dropdown, DropdownButton } from 'react-bootstrap';
import { Syringe, Printer } from 'lucide-react';

import {
  SECTIONS,
  VACCINES,
  parseVaccinationCardDetailsToState,
  DETAILS_SCHEMA_VERSION
} from '../constants/vaccinationCardCatalog';
import { buildPrintDocument, openPrintWindow } from '../utils/printUtility';
import {
  sanitizeDoseNotesForDisplay,
  doseHasPromotionEvidence,
  openVaccinationCardPromotionEvidence
} from '../utils/vaccinationCardDoseHelpers';
import { Download } from 'lucide-react';
const DEFAULT_OPEN_SECTION = SECTIONS[0]?.id || 'core';

function formatDoseDate(isoOrYmd) {
  if (!isoOrYmd) return '—';
  const s = String(isoOrYmd).trim();
  const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function boolLabel(v) {
  return v === 'yes' ? 'Yes' : v === 'no' ? 'No' : '—';
}

export default function VaccinationCardViewModal({
  show,
  onHide,
  details,
  residentName,
  canEditDoses = false,
  onSaveDetails = null
}) {
  const [openSectionKey, setOpenSectionKey] = useState(DEFAULT_OPEN_SECTION);
  const [editableState, setEditableState] = useState(() => parseVaccinationCardDetailsToState(details));
  const [editingDoseKey, setEditingDoseKey] = useState(null);
  const [editingDraft, setEditingDraft] = useState({ date: '', notes: '' });
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const parsedDetails = useMemo(() => {
    if (!details) return {};
    if (typeof details === 'object') return details;
    if (typeof details === 'string') {
      try {
        return JSON.parse(details);
      } catch {
        return {};
      }
    }
    return {};
  }, [details]);

  const vaccinesState = editableState;

  const totalDoses = useMemo(() => {
    return VACCINES.reduce((sum, v) => sum + (vaccinesState[v.id]?.doses || []).length, 0);
  }, [vaccinesState]);

  useEffect(() => {
    if (show) {
      setOpenSectionKey(DEFAULT_OPEN_SECTION);
    }
  }, [show, details]);

  useEffect(() => {
    setEditableState(parseVaccinationCardDetailsToState(details));
    setEditingDoseKey(null);
    setEditingDraft({ date: '', notes: '' });
    setSaveError('');
    setIsSaving(false);
  }, [details, show]);

  const sectionVaccines = (sectionId) => VACCINES.filter((v) => v.sectionId === sectionId);
  const tbScreening = parsedDetails.tbScreening || {};
  const vaccineEvidenceFiles = Array.isArray(parsedDetails.vaccineEvidenceFiles)
    ? parsedDetails.vaccineEvidenceFiles
    : [];

  const handlePrint = () => {
    const rowsBySection = SECTIONS.map((sec) => {
      const vaccinesHtml = sectionVaccines(sec.id).map((v) => {
        const doses = vaccinesState[v.id]?.doses || [];
        if (doses.length === 0) {
          return `
            <div style="padding: 12px 0; border-top: 1px solid #eaedf2;">
              <div style="font-weight: 600;">${v.name}</div>
              <div style="color: #78829D; margin-top: 4px;">${v.description || ''}</div>
              <div style="margin-top: 8px; color: #78829D; font-style: italic;">No doses recorded</div>
            </div>
          `;
        }

        const doseRows = doses.map((d, idx) => {
          const notesDisp = sanitizeDoseNotesForDisplay(d.notes);
          const loc = d.promotionAdministrationLocation ? String(d.promotionAdministrationLocation).trim() : '';
          const hasFile = doseHasPromotionEvidence(d);
          const locHtml = loc
            ? `<div style="font-size: 14px; margin-top: 4px;"><span style="font-weight: 600; color: #78829D;">Location: </span><span style="color: #222;">${loc.replace(/</g, '&lt;')}</span></div>`
            : '';
          const proofHtml = hasFile
            ? `<div style="font-size: 13px; margin-top: 4px; color: #0d6efd;">Proof file: ${String(d.promotionOriginalFileName || 'uploaded document').replace(/</g, '&lt;')}</div>`
            : '';
          return `
          <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; border-radius: 7px; border: 1px solid #eaedf2; background: #f8fafd; padding: 10px 12px; margin-bottom: 8px;">
            <div style="display: flex; align-items: center; gap: 16px; flex: 1; min-width: 0;">
              <div style="min-width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; background: #e2e6ef; font-weight: 600; border-radius: 7px; font-size: 14px;">
                ${idx + 1}
              </div>
              <div style="flex: 1; min-width: 0;">
                <div style="display: flex; gap: 24px; flex-wrap: wrap;">
                  <div style="font-size: 15px;">
                    <span style="font-weight: 500; color: #78829D;">Vaccination Date: </span>
                    <span style="font-weight: 500; color: #222;">${formatDoseDate(d.date)}</span>
                  </div>
                  <div style="font-size: 15px;">
                    <span style="font-weight: 500; color: #78829D;">Notes: </span>
                    <span style="color: #222;">${notesDisp ? notesDisp.replace(/</g, '&lt;').replace(/\n/g, '<br/>') : '—'}</span>
                  </div>
                </div>
                ${locHtml}
                ${proofHtml}
              </div>
            </div>
          </div>
        `;
        }).join('');

        const vaccineProofHtml = vaccinesState[v.id]?.cardEvidencePath
          ? `<div style="margin-top: 8px; color: #0d6efd; font-size: 13px;">Card evidence: ${String(
              vaccinesState[v.id]?.cardEvidenceOriginalFileName || 'uploaded document'
            ).replace(/</g, '&lt;')}</div>`
          : '';

        return `
          <div style="padding: 12px 0; border-top: 1px solid #eaedf2;">
            <div style="font-weight: 600;">${v.name}</div>
            <div style="color: #78829D; margin-top: 4px;">${v.description || ''}</div>
            <div style="margin-top: 10px;">
              ${doseRows}
            </div>
            ${vaccineProofHtml}
          </div>
        `;
      }).join('');

      return `
        <div class="print-section page-break-avoid">
          <div style="display: flex; align-items: center; justify-content: start; gap: 12px;">
            <div style="width: 6px; min-width: 6px; height: 2.5em; background: #0d6efd; border-radius: 4px; margin-top: 2px; margin-right: 4px;"></div>
            <div style="flex: 1;">
              <h2 style="border-bottom: none; margin-bottom: 0;">${sec.title}</h2>
              <div style="color: #78829D; margin-top: -6px; margin-bottom: 8px;">${sec.subtitle || ''}</div>
            </div>
          </div>
          ${vaccinesHtml}
        </div>
      `;
    }).join('');

    const mappedEvidenceRows = vaccineEvidenceFiles
      .map((row) => {
        const labels = Array.isArray(row?.vaccineIds)
          ? row.vaccineIds
              .map((id) => VACCINES.find((v) => v.id === id)?.name)
              .filter(Boolean)
              .join(', ')
          : '';
        return `<div><span style="font-weight:600;">${String(row?.originalFileName || 'File').replace(/</g, '&lt;')}:</span> ${String(labels || 'No mapped vaccines').replace(/</g, '&lt;')}</div>`;
      })
      .join('');
    const evidenceRows = `
      <div class="print-section page-break-avoid">
        <h2>Vaccine Evidence Files</h2>
        <div style="padding-top: 8px;">
          ${mappedEvidenceRows || '<div>None submitted</div>'}
        </div>
      </div>
    `;
    const tbRows = `
      <div class="print-section page-break-avoid">
        <h2>TB Screening</h2>
        <div style="padding-top: 8px;">
          <div><span style="font-weight:600;">Diagnosed with TB:</span> ${boolLabel(tbScreening.diagnosedTb)}</div>
          <div><span style="font-weight:600;">Treated:</span> ${boolLabel(tbScreening.treatedTb)}</div>
          <div><span style="font-weight:600;">Treatment regimen:</span> ${tbScreening.treatmentRegimen || '—'}</div>
          <div><span style="font-weight:600;">BCG received:</span> ${boolLabel(tbScreening.bcgReceived)}</div>
          <div><span style="font-weight:600;">BCG date received:</span> ${tbScreening.bcgReceivedDate || '—'}</div>
          <div><span style="font-weight:600;">IGRA done:</span> ${boolLabel(tbScreening.igraDone)}</div>
          <div><span style="font-weight:600;">IGRA result:</span> ${tbScreening.igraResult || '—'}</div>
          <div><span style="font-weight:600;">PPD done:</span> ${boolLabel(tbScreening.ppdDone)}</div>
          <div><span style="font-weight:600;">PPD result:</span> ${tbScreening.ppdResult || '—'}</div>
          <div><span style="font-weight:600;">Chest X-ray done:</span> ${boolLabel(tbScreening.chestXrayDone)}</div>
          <div><span style="font-weight:600;">Chest X-ray evidence:</span> ${
            tbScreening.chestXrayEvidenceOriginalFileName || '—'
          }</div>
        </div>
      </div>
    `;

    const htmlContent = buildPrintDocument({
      title: 'Vaccination Card Report',
      headerTitle: 'Vaccination Card Report',
      additionalHeaderInfo: [
        { label: 'Generated', value: new Date().toLocaleString() }
      ], 
       headerSubtitle: residentName ? `<span style="font-weight:600;">Resident</span><br/>${residentName}` : '',
      content: rowsBySection + evidenceRows + tbRows
    });

    openPrintWindow(htmlContent);
  };

  const parseDoseKey = (doseKey) => {
    const [vaccineId, idxRaw] = String(doseKey || '').split('::');
    const doseIndex = Number(idxRaw);
    if (!vaccineId || !Number.isFinite(doseIndex)) return null;
    return { vaccineId, doseIndex };
  };

  const saveNextState = async (nextState) => {
    if (!canEditDoses || typeof onSaveDetails !== 'function') return false;
    const nextDetailsPayload = {
      ...(parsedDetails && typeof parsedDetails === 'object' ? parsedDetails : {}),
      schemaVersion:
        Number(parsedDetails?.schemaVersion) > 0
          ? Number(parsedDetails.schemaVersion)
          : DETAILS_SCHEMA_VERSION,
      vaccines: VACCINES.reduce((acc, def) => {
        const src = nextState?.[def.id];
        const doses = Array.isArray(src?.doses) ? src.doses : [];
        acc[def.id] = {
          doses,
          ...(src?.cardEvidencePath ? { cardEvidencePath: String(src.cardEvidencePath) } : {}),
          ...(src?.cardEvidenceOriginalFileName
            ? { cardEvidenceOriginalFileName: String(src.cardEvidenceOriginalFileName) }
            : {})
        };
        return acc;
      }, {})
    };
    setIsSaving(true);
    setSaveError('');
    try {
      const ok = await onSaveDetails(nextDetailsPayload);
      if (!ok) {
        setSaveError('Failed to save vaccination card changes.');
        return false;
      }
      return true;
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to save vaccination card changes.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const beginEditDose = (vaccineId, doseIndex) => {
    const dose = editableState?.[vaccineId]?.doses?.[doseIndex];
    if (!dose) return;
    setEditingDoseKey(`${vaccineId}::${doseIndex}`);
    setEditingDraft({
      date: dose.date || '',
      notes: sanitizeDoseNotesForDisplay(dose.notes || '')
    });
  };

  const cancelEditDose = () => {
    setEditingDoseKey(null);
    setEditingDraft({ date: '', notes: '' });
  };

  const saveEditedDose = async () => {
    const parsed = parseDoseKey(editingDoseKey);
    if (!parsed) return;
    const { vaccineId, doseIndex } = parsed;
    const existingDose = editableState?.[vaccineId]?.doses?.[doseIndex];
    if (!existingDose) return;

    const nextState = JSON.parse(JSON.stringify(editableState));
    const nextDose = {
      ...existingDose,
      date: String(editingDraft.date || '').trim(),
      notes: String(editingDraft.notes || '').trim()
    };
    nextState[vaccineId].doses[doseIndex] = nextDose;

    const saved = await saveNextState(nextState);
    if (!saved) return;
    setEditableState(nextState);
    cancelEditDose();
  };

  const deleteDose = async (vaccineId, doseIndex) => {
    const dose = editableState?.[vaccineId]?.doses?.[doseIndex];
    if (!dose) return;
    const confirmed = window.confirm('Delete this dose from the vaccination card?');
    if (!confirmed) return;
    const nextState = JSON.parse(JSON.stringify(editableState));
    nextState[vaccineId].doses.splice(doseIndex, 1);
    const saved = await saveNextState(nextState);
    if (!saved) return;
    setEditableState(nextState);
    if (editingDoseKey === `${vaccineId}::${doseIndex}`) {
      cancelEditDose();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-bottom-0 d-flex flex-row align-items-center gap-2">
        <span className="d-inline-flex align-items-center justify-content-center rounded-2 bg-primary text-white p-2">
          <Syringe /> 
        </span>
        <div className="d-flex flex-column flex-grow-1">
          <h6 className="fw-semibold mb-0">Vaccination Card</h6>
          {residentName ? (
            <p className="small text-primary mb-0">
              Resident: <span className="fw-semibold">{residentName}</span>
            </p>
          ) : null}
        </div>
        <Button variant="outline-primary" size="sm" onClick={handlePrint}>
          <Printer />
          <span className="d-none d-md-inline ms-2">Print</span>
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-0">
        {saveError ? (
          <Alert variant="danger" className="py-2 small mb-3">
            {saveError}
          </Alert>
        ) : null}
        {/*<div className="mb-3 p-3 rounded border bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <span className="small text-muted text-uppercase">Total doses recorded</span>
            <span className="fw-semibold fs-5">{totalDoses}</span>
          </div>
        </div>*/}

        <Accordion
          activeKey={openSectionKey}
          onSelect={(key) => {
            if (key == null || key === '') {
              return;
            }
            setOpenSectionKey(key);
          }}
        >
          {SECTIONS.map((sec) => (
            <Accordion.Item eventKey={sec.id} key={sec.id}>
              <Accordion.Header>
                <div className="d-flex align-items-center gap-2 w-100 me-2">
                  <span
                    className="rounded-2 flex-shrink-0"
                    style={{
                      width: 8,
                      minHeight: 40,
                      backgroundColor:
                        sec.id === 'core' ? '#0d6efd' : sec.id === 'earlyChildhood' ? '#6f42c1' : '#198754'
                    }}
                  />
                  <div>
                    <div className="fw-semibold">{sec.title}</div>
                    <div className="small text-muted">{sec.subtitle}</div>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="pt-2">
                {sectionVaccines(sec.id).map((v) => {
                  const doses = vaccinesState[v.id]?.doses || [];
                  const mappedEvidenceFiles = vaccineEvidenceFiles.filter(
                    (row) => Array.isArray(row.vaccineIds) && row.vaccineIds.includes(v.id)
                  );
                  return (
                    <div key={v.id} className=" pb-3">
                      <div>
                        <span className="fw-semibold">{v.name}</span> (
                          <span className="small text-muted">{v.minDoses === v.maxDoses
                            ? `${v.minDoses} dose${v.minDoses > 1 ? 's' : ''} needed`
                            : `${v.minDoses}-${v.maxDoses} doses needed`
                          }
                        </span>
                        )
                      </div>
                 
                      <div className="small text-muted mb-2">{v.description}</div>
           
                      {doses.length === 0 ? (
                        <div className="text-muted small fst-italic">No doses recorded</div>
                      ) : (
                        <div>
                          {doses.map((d, idx) => {
                            const doseKey = `${v.id}::${idx}`;
                            const isEditing = editingDoseKey === doseKey;
                            return (
                            <div
                              key={`${v.id}-${idx}`}
                              className="d-flex align-items-stretch align-items-md-center gap-2 gap-md-3 mb-2 p-2 rounded custom-border-alt-light border-alt-light custom-hover-bg-alt flex-wrap flex-md-nowrap"
                            >
                              <span className="d-inline-flex align-items-center justify-content-center rounded-2 bg-secondary text-white flex-shrink-0" style={{ width: 32, height: 32 }}>
                                <Syringe />
                              </span>
                              <div className="d-flex flex-column flex-sm-row align-items-sm-start gap-2 gap-sm-3 flex-grow-1 min-w-0 small">
                                <span className="fw-semibold text-nowrap">Dose #{idx + 1}</span>
                                {isEditing ? (
                                  <>
                                    <span className="flex-shrink-0">
                                      <span className="fw-semibold d-block">Date received</span>
                                      <Form.Control
                                        type="date"
                                        size="sm"
                                        value={editingDraft.date}
                                        onChange={(e) =>
                                          setEditingDraft((prev) => ({ ...prev, date: e.target.value }))
                                        }
                                      />
                                    </span>
                                    <span className="text-muted min-w-0" style={{ minWidth: 260 }}>
                                      <span className="fw-semibold text-body d-block">Notes</span>
                                      <Form.Control
                                        as="textarea"
                                        rows={1}
                                        size="sm"
                                        value={editingDraft.notes}
                                        onChange={(e) =>
                                          setEditingDraft((prev) => ({ ...prev, notes: e.target.value }))
                                        }
                                      />
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="flex-shrink-0">
                                      <span className="fw-semibold">Date received:</span> {formatDoseDate(d.date)}
                                    </span>
                                    <span className="text-muted min-w-0" style={{ whiteSpace: 'pre-wrap' }}>
                                      <span className="fw-semibold text-body">Notes:</span>{' '}
                                      {sanitizeDoseNotesForDisplay(d.notes) ? (
                                        sanitizeDoseNotesForDisplay(d.notes)
                                      ) : (
                                        <span className="fst-italic text-muted">No notes</span>
                                      )}
                                    </span>
                                  </>
                                )}
                              </div>
                              {mappedEvidenceFiles.length > 0 ? (
                                <div className="d-flex flex-column gap-1">
                                  {mappedEvidenceFiles.map((row, idx) => (
                                    <Button
                                      key={`${v.id}-mapped-evidence-${idx}`}
                                      size="sm"
                                      variant="outline-primary"
                                      className=" d-flex align-items-center "
                                      onClick={() => openVaccinationCardPromotionEvidence(row.path)}
                                    >
                                      <Download/> <span className="d-none d-md-inline ms-2">Documentation</span> {/*({row.originalFileName || 'file'})*/}
                                    </Button>
                                  ))}
                                </div>
                              ) : null}
                              <div className="d-flex flex-row align-items-stretch align-items-md-center gap-2 flex-shrink-0 ms-md-auto text-end small">
                                {d.promotionAdministrationLocation ? (
                                  <div className="text-muted">
                                    <span className="fw-semibold text-body">Location:</span>{' '}
                                    {String(d.promotionAdministrationLocation)}
                                  </div>
                                ) : null}
                                {doseHasPromotionEvidence(d) ? (
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="text-nowrap"
                                    onClick={() => openVaccinationCardPromotionEvidence(d.promotionEvidencePath)}
                                  >
                                    View proof
                                  </Button>
                                ) : null}
                                {canEditDoses ? (
                                  <DropdownButton
                                    id={`dose-actions-${v.id}-${idx}`}
                                    title={isSaving ? <Spinner animation="border" size="sm" /> : <i className="bi bi-three-dots-vertical"></i>}
                                    size="sm"
                                    variant="light-lite"
                                    className="no-btn-arrow"
                                    disabled={isSaving}
                                  >
                                    {isEditing ? (
                                      <>
                                        <Dropdown.Item onClick={saveEditedDose} disabled={isSaving}>
                                          Save
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={cancelEditDose} disabled={isSaving}>
                                          Cancel
                                        </Dropdown.Item>
                                      </>
                                    ) : (
                                      <>
                                        <Dropdown.Item onClick={() => beginEditDose(v.id, idx)} disabled={isSaving}>
                                          Edit
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={() => deleteDose(v.id, idx)}
                                          disabled={isSaving}
                                          className="text-danger"
                                        >
                                          Delete
                                        </Dropdown.Item>
                                      </>
                                    )}
                                  </DropdownButton>
                                ) : null}
                              </div>
                            </div>
                          )})}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
        <div className="mt-3 border rounded">
          <div className="d-flex flex-column px-3 pt-3 pb-2">
            <h6 className="fw-semibold mb-0">Vaccine evidence files</h6>
            <p className="small text-muted mb-0">View all vaccine evidence files submitted by the resident</p>
          </div>
          {vaccineEvidenceFiles.length === 0 ? (
            <div className="small text-muted fst-italic p-3 pt-0">No mapped vaccine evidence files submitted.</div>
          ) : (
            <Table responsive>
              <thead>
                <tr className="custom-header-bg">
                  <th>File</th>
                  <th>Mapped vaccines</th>
                  <th >Action</th>
                </tr>
              </thead>
              <tbody>
                {vaccineEvidenceFiles.map((row, idx) => {
                  const labels = Array.isArray(row?.vaccineIds)
                    ? row.vaccineIds
                        .map((id) => VACCINES.find((v) => v.id === id)?.name)
                        .filter(Boolean)
                    : [];
                  return (
                    <tr key={`vax-evidence-row-${idx}`}>
                      <td className="small">{row?.originalFileName || 'Uploaded file'}</td>
                      <td className="small text-muted" style={{ width: '50%' }}>{labels.length ? labels.join(', ') : '—'}</td>
                      <td>
                        {row?.path ? (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className=" d-flex align-items-center"
                            onClick={() => openVaccinationCardPromotionEvidence(row.path)}
                          >
                            <Download/> <span className="d-none d-md-inline ms-2">Download</span>
                          </Button>
                        ) : (
                          <span className="small text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
        <div className="mt-3 p-3 border rounded">
          <h6 className="fw-semibold mb-0">TB screening</h6>
          <p className="small text-muted mb-3">Complete TB history and screening documentation.</p>
          <div className="row small">
            {(() => {
              // Collect all fields and their render logic for both columns
              const leftFields = [
                tbScreening.diagnosedTb !== undefined && tbScreening.diagnosedTb !== ''
                  ? (
                    <div className="d-flex justify-content-between" key="diagnosedTb">
                      <span className="fw-semibold">History of TB:</span>
                      <span>{boolLabel(tbScreening.diagnosedTb)}</span>
                    </div>
                  ) : null,
                tbScreening.treatedTb !== undefined && tbScreening.treatedTb !== ''
                  ? (
                    <div className="d-flex justify-content-between" key="treatedTb">
                      <span className="fw-semibold">Did you receive treatment?</span>
                      <span>{boolLabel(tbScreening.treatedTb)}</span>
                    </div>
                  ) : null,
                tbScreening.treatmentRegimen
                  ? (
                    <div className="d-flex justify-content-between" key="treatmentRegimen">
                      <span className="fw-semibold">Treatment regimen:</span>
                      <span>{tbScreening.treatmentRegimen}</span>
                    </div>
                  ) : null,
                tbScreening.bcgReceived !== undefined && tbScreening.bcgReceived !== ''
                  ? (
                    <div className="d-flex justify-content-between" key="bcgReceived">
                      <span className="fw-semibold">Have you received the BCG vaccine?</span>
                      <span>{boolLabel(tbScreening.bcgReceived)}</span>
                    </div>
                  ) : null,
                tbScreening.bcgReceivedDate
                  ? (
                    <div className="d-flex justify-content-between" key="bcgReceivedDate">
                      <span className="fw-semibold">BCG date received:</span>
                      <span>{tbScreening.bcgReceivedDate}</span>
                    </div>
                  ) : null,
              ].filter(Boolean);

              const rightFields = [
                tbScreening.igraDone !== undefined && tbScreening.igraDone !== ''
                  ? (
                    <div className="d-flex justify-content-between" key="igraDone">
                      <span className="fw-semibold">Have you had a TB Blood Test (IGRA) done?</span>
                      <span>{boolLabel(tbScreening.igraDone)}</span>
                    </div>
                  ) : null,
                tbScreening.igraResult
                  ? (
                    <div className="d-flex justify-content-between" key="igraResult">
                      <span className="fw-semibold">IGRA result:</span>
                      <span>{tbScreening.igraResult}</span>
                    </div>
                  ) : null,
                tbScreening.ppdDone !== undefined && tbScreening.ppdDone !== ''
                  ? (
                    <div className="d-flex justify-content-between" key="ppdDone">
                      <span className="fw-semibold">Have you had a PPD skin test?</span>
                      <span>{boolLabel(tbScreening.ppdDone)}</span>
                    </div>
                  ) : null,
                tbScreening.ppdResult
                  ? (
                    <div className="d-flex justify-content-between" key="ppdResult">
                      <span className="fw-semibold">PPD result:</span>
                      <span>{tbScreening.ppdResult}</span>
                    </div>
                  ) : null,
                tbScreening.chestXrayDone !== undefined && tbScreening.chestXrayDone !== ''
                  ? (
                    <div className="d-flex justify-content-between" key="chestXrayDone">
                      <span className="fw-semibold">Have you had a chest X-ray?</span>
                      <span>{boolLabel(tbScreening.chestXrayDone)}</span>
                    </div>
                  ) : null,
                tbScreening.chestXrayEvidencePath
                  ? (
                    <div className="d-flex justify-content-between mt-2" key="chestXrayEvidencePath">
                      <span className="fw-semibold"></span>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => openVaccinationCardPromotionEvidence(tbScreening.chestXrayEvidencePath)}
                      >
                        View chest X-ray evidence ({tbScreening.chestXrayEvidenceOriginalFileName || 'file'})
                      </Button>
                    </div>
                  ) : null,
              ].filter(Boolean);

              // Concatenate and split evenly down the middle (left then right)
              const total = leftFields.length + rightFields.length;
              const allFields = [...leftFields, ...rightFields];
              const mid = Math.ceil(total / 2);
              const col1 = allFields.slice(0, mid);
              const col2 = allFields.slice(mid);

              return (
                <>
                  <div className="col-12 col-md-6 d-flex flex-column gap-1">
                    {col1.length > 0 ? col1 : <div className="text-muted small">—</div>}
                  </div>
                  <div className="col-12 col-md-6 d-flex flex-column gap-1">
                    {col2.length > 0 ? col2 : <div className="text-muted small">—</div>}
                  </div>
                </>
              );
            })()}
 
          </div>
     
     
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button variant="primary" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
